<template>
  <b-modal
    id="modal-approval-online-doa"
    :title="isEdit ? 'Edit Approval' : 'Add New Approval'"
    centered
    hide-footer
    hide-header
  >
    <div class="p-3">
      <b-form autocomplete="off">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h4>
            <b>{{ isEdit ? "Edit Approval" : "Add New Approval" }}</b>
          </h4>
          <img class="pointer" src="@/assets/btn-close.png" @click="close" />
        </div>
        <validation-observer ref="form" slim>
          <validation-provider
            name="Company"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Company"
              label-size="sm"
              :invalid-feedback="errors[0]"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.company_id"
                placeholder="Select company"
                :options="options.companies"
                label="name"
                :class="{ 'is-invalid': errors.length }"
                :reduce="({ id }) => id"
                clearable
                :disabled="!isAdmin"
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            name="Feature"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Feature"
              label-size="sm"
              :invalid-feedback="errors[0]"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.feature"
                placeholder="Select feature"
                :options="options.feature_list"
                :class="{ 'is-invalid': errors.length }"
                clearable
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            name="Level"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Level"
              label-size="sm"
              :invalid-feedback="errors[0]"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.level"
                placeholder="Select level"
                :options="options.level_list"
                :class="{ 'is-invalid': errors.length }"
                clearable
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            name="Group Level"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Group Level"
              label-size="sm"
              :invalid-feedback="errors[0]"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.group_level"
                placeholder="Select group level"
                :options="options.group_level_list"
                :class="{ 'is-invalid': errors.length }"
                clearable
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            name="Organization Level"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Organization Level"
              label-size="sm"
              :invalid-feedback="errors[0]"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.organization_level"
                placeholder="Select organization level"
                :options="options.organization_level_list"
                :class="{ 'is-invalid': errors.length }"
                @input="checkOrganization(form.organization_level)"
                clearable
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-if="form.organization_level !== 'contractor'"
            name="Division"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Division"
              label-size="sm"
              :invalid-feedback="errors[0]"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.division_id"
                placeholder="Select division"
                :options="options.divisions"
                label="name"
                :class="{ 'is-invalid': errors.length }"
                :reduce="({ id }) => id"
                @input="form.organization_level === 'department' ? fetchDepartments(form.division_id) : fetchUsers(null)"
                clearable
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-if="form.organization_level == 'department'"
            name="Department"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Department"
              :invalid-feedback="errors[0]"
              label-size="sm"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.department_id"
                placeholder="Select departement"
                :options="options.departements"
                label="name"
                :class="{ 'is-invalid': errors.length }"
                :reduce="({ id }) => id"
                @input="fetchUsers(form.department_id)"
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-if="form.organization_level !== 'contractor'"
            name="User"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="User"
              :invalid-feedback="errors[0]"
              label-size="sm"
              label-class="font-weight-bold"
            >
              <v-select
                v-model="form.users"
                placeholder="Select users"
                label="fullname"
                :options="options.users"
                :class="{ 'is-invalid': errors.length }"
                multiple
                clearable
                @input="checkUserLimit"
                @search="searchUser($event)"
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-if="form.organization_level !== 'contractor'"
            name="User Default Approve"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="User Default Approve"
              :invalid-feedback="errors[0]"
              label-size="sm"
              label-class="font-weight-bold"
            >
              <v-select
                ref="userDefaultApprove"
                v-model="form.defaultApprover"
                placeholder="Select User Default Approve"
                label="fullname"
                :options="options.userDefault"
                :class="{ 'is-invalid': errors.length }"
              />
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-if="form.organization_level === 'contractor'"
            name="Role"
            rules="required"
            v-slot="{ errors }"
            slim
            class="flex-fill"
          >
            <b-form-group
              label="Role"
              :invalid-feedback="errors[0]"
              label-size="sm"
              label-class="font-weight-bold"
            >
              <v-select
                ref="role"
                v-model="form.role"
                placeholder="Select User Default Approve"
                label="name"
                :options="options.roles"
                :class="{ 'is-invalid': errors.length }"
              />
            </b-form-group>
          </validation-provider>
        </validation-observer>
        <div class="d-flex justify-content-end align-items-center mt-5">
          <b-button
            class="btn-reset"
            variant="outline-info"
            @click="close"
          >
            Cancel
          </b-button>
          <b-button class="btn-custom ml-2 " variant="info" @click="submit">
            Submit
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import api from '@/api'

export default {
  props: {
    isEdit: {
      types: Boolean,
      default: () => false
    },
    isAdmin: {
      types: Boolean,
      default: () => false
    },
    form: {
      types: Object,
      default: () => ({
        division_id: null,
        department_id: null,
        users: []
      })
    },
    options: {
      types: Object,
      default: () => ({})
    }
  },
  methods: {
    async fetchDivisions() {
      this.options.divisions = await api.department.divisions()
    },
    async fetchDepartments(divisionId) {
      this.form.users = []
      this.form.defaultApprover = null
      this.form.department_id = null
      if (this.form.division_id) {
        this.options.departements = await api.department.list({
          division_id: divisionId
        })
      } else {
        this.options.departements = await api.department.list()
      }
    },
    async fetchUsers(departmenId) {
      this.form.users = []
      this.form.defaultApprover = null

      if (this.form.division_id) {
        const filterValue = departmenId ? `department_id:${departmenId},division_id:${this.form.division_id}` : `division_id:${this.form.division_id}`
        const employeeDepartments = await api.employee.list({
          filter_value: filterValue,
          direction: 'DESC',
          company_id: this.form.company_id,
          search: ''
        })
        this.options.users = employeeDepartments.data.users
      }
    },
    async searchUser(keyword) {
      if (this.form.division_id && this.form.department_id) {
        const employeeDepartments = await api.employee.list({
          filter_value: `department_id:${this.form.department_id},division_id:${this.form.division_id}`,
          page: 1,
          limit: 20,
          direction: 'DESC',
          company_id: '',
          search: keyword
        })
        this.options.users = employeeDepartments.data.users
      } else {
        const employeeDepartments = await api.employee.list({
          filter_value: '',
          page: 1,
          limit: 20,
          direction: 'DESC',
          company_id: '',
          search: keyword
        })
        this.options.users = employeeDepartments.data.users
      }
    },
    async submit() {
      const valid = await this.$refs.form.validate()
      if (!valid) {
        return false
      }

      const data = {
        feature: this.form.feature,
        level: this.form.level,
        group_level: this.form.group_level,
        org_level: this.form.organization_level,
        division_id: this.form.division_id,
        department_id: this.form.department_id,
        company_id: this.form.company_id.id || this.form.company_id,
        users: [],
        role_id: this.form.role ? this.form.role.id : null
      }
      if (this.form.organization_level === 'contractor') this.form.users = []
      this.form.users.map(v => data.users.push({
        user_id: v.id,
        is_default: v.id === this.form.defaultApprover.id
      }))
      this.$emit('submit', data)
      this.$bvModal.hide('modal-approval-online-doa')
    },
    checkOrganization(organizationLevel) {
      this.form.users = []
      this.form.defaultApprover = null
      this.options.users = []
      this.options.userDefault = []
      this.form.role = null

      if (organizationLevel === 'department' && this.form.division_id) {
        this.fetchDepartments(this.form.division_id)
      } else {
        this.form.department_id = null
        this.fetchUsers(null)
      }
    },
    checkUserLimit() {
      this.form.defaultApprover = null
      this.options.userDefault = []
      if (this.form.users.length > 3) {
        this.$nextTick(() => {
          this.$bvToast.toast('max users in users field are 3 people', {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
        this.form.users.pop()
      }
      this.options.userDefault = this.form.users
    },
    close() {
      this.$emit('cancel')
      this.$bvModal.hide('modal-approval-online-doa')
      this.resetForm()
    },
    resetForm() {
      this.form.department_id = null
      this.form.division_id = null
      this.form.users = []
      this.form.defaultApprover = null
    }
  }
}
</script>
<style lang="scss" scoped>
.icon-hide {
  position: absolute;
  right: 25px;
  top: 55%;
  transform: translateY(-50%);
  cursor: pointer;
}

.icon-hide-no-error {
  position: absolute;
  right: 7px;
  top: 70%;
  transform: translateY(-50%);
  cursor: pointer;
}
</style>
